import { useSitrepSectionWizardContext } from '../SitrepSectionWizardContext';
import UploadedFileBadge from '../common/UploadedFileBadge';
import { StylishNewCheckbox } from '../../../DesignSystems/New/StylishNewCheckbox';
import { DATA_STEP, OUTPUT_FORMATTING_LIST, SECTION_STEP } from '../types';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import EditIcon from '../../../../assets/images/icon__edit.svg';
import React, { useCallback } from 'react';
import NoMarginH4 from '../common/NoMarginH4';

interface EditButtonRowProps {
  onClick: () => void;
}
const EditButtonRow = ({ onClick }: EditButtonRowProps) => (
  <div className="d-flex flex-row justify-content-end">
    <StylishNewButton className="dark-background-button" onClick={onClick}>
      <img src={EditIcon} alt="" />
    </StylishNewButton>
  </div>
);

const FinalizeSection = () => {
  const { sectionName, aiPrompt, handleEditButtonClick } =
    useSitrepSectionWizardContext();

  const editSectionOnClick = useCallback(
    () => handleEditButtonClick(SECTION_STEP),
    [handleEditButtonClick]
  );

  return (
    <div className="d-flex flex-column gap-2">
      <EditButtonRow onClick={editSectionOnClick} />
      <NoMarginH4>Section Name:</NoMarginH4>
      <p>{sectionName}</p>
      <NoMarginH4>AI Prompt:</NoMarginH4>
      <p>{aiPrompt}</p>
    </div>
  );
};

const FinalizeData = () => {
  const { selectedDataSources, uploadedFiles, handleEditButtonClick } =
    useSitrepSectionWizardContext();

  const editDataOnClick = useCallback(
    () => handleEditButtonClick(DATA_STEP),
    [handleEditButtonClick]
  );

  return (
    <>
      <EditButtonRow onClick={editDataOnClick} />
      <div className="d-flex flex-column gap-3">
        <NoMarginH4>Data Sources:</NoMarginH4>
        {selectedDataSources.length > 0 ? (
          selectedDataSources.map((source) => <p key={source}>{source}</p>)
        ) : (
          <p>No data sources selected</p>
        )}
        <NoMarginH4>Uploaded Files:</NoMarginH4>
        {uploadedFiles.length > 0 ? (
          uploadedFiles.map((file, i) => (
            <UploadedFileBadge key={i} file={file} />
          ))
        ) : (
          <p>No files uploaded</p>
        )}
      </div>
    </>
  );
};

const FinalizeOutput = () => {
  const { outputFormatting, handleSetOutputFormatting } =
    useSitrepSectionWizardContext();

  return (
    <div className="d-flex flex-column gap-4">
      <NoMarginH4>Choose Output Formatting</NoMarginH4>
      <div className="d-flex flex-column gap-4">
        {OUTPUT_FORMATTING_LIST.map((format) => (
          <div key={format} className="d-flex align-items-center">
            <StylishNewCheckbox
              label={format}
              checked={outputFormatting.includes(format)}
              onClick={(e) => handleSetOutputFormatting(e, format)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * The finalize component is composed of three sections:
 *
 * 1. FinalizeSection: Displays the section name and AI prompt.
 * 2. FinalizeData: Displays the selected data sources and uploaded files.
 * 3. FinalizeOutput: Allows the user to choose output formatting options.
 */
const Finalize = () => (
  <div className="finalize-section">
    <FinalizeSection />
    <hr />
    <FinalizeData />
    <hr />
    <FinalizeOutput />
  </div>
);

export default Finalize;
